import { Dropdown, Spin } from 'antd';
import { useEffect, useState } from 'react';
import { MoreOutlined } from '@ant-design/icons';
import { PermissionCombinationType, useAccess } from '@sitemax/smx-permission';

import { Copy } from './ItemActions/Copy';
import { Move } from './ItemActions/Move';
import { Delete } from './ItemActions/Delete';
import { Rename } from './ItemActions/Rename';
import { Sharing } from './ItemActions/Sharing';
import { PublicLink } from './ItemActions/PublicLink';
import { History } from './ItemActions/History';
import { Versions } from './ItemActions/Versions';
import { Download } from './ItemActions/Download';
import { Category, Document } from 'interfaces/documents';
import { useAxios } from '../../../utilities/axios';
import { useAppSelector } from '../../../utilities/hooks';
import { isDocument } from '../../../utilities/constants';
import { MenuItemType } from 'antd/es/menu/interface';

const ItemActionList = ({ fetchData, row, filterActions }: { fetchData: () => void; row: Category | Document; filterActions?: (actions: MenuItemType[]) => MenuItemType[]; }) => {
    const accessToken = useAppSelector((state) => state.auth.accessToken);
    const currentTenant = useAppSelector((state) => state.tenants.currentTenant);

    const axios = useAxios(accessToken, currentTenant);

    const [loading, setLoading] = useState<boolean>(false);
    const [documentAccess, setDocumentAccess] = useState<'manager' | 'editor' | 'viewer' | null>(null);

    useEffect(() => {
        (async () => {
            try {
                setLoading(true);
                const response = await axios?.get(`api/v1/list/documentAccess?documentId=${row.id}`, {});
                setDocumentAccess(response?.data.data.documentAccess);
            } catch (error: any) {
                console.error('Problem to get access document from server');
            }
            setLoading(false);
        })();
    }, []);

    const isProjectDocument = row.is_project_document;
    const viewPermissions: PermissionCombinationType[] = isProjectDocument
        ? ['files.project.view']
        : ['files.all.view'];
    const createPermissions: PermissionCombinationType[] = isProjectDocument
        ? ['files.project.create']
        : ['files.all.create'];
    const editPermissions: PermissionCombinationType[] = isProjectDocument
        ? ['files.project.edit']
        : ['files.all.edit'];
    const managePermissions: PermissionCombinationType[] = isProjectDocument
        ? ['files.project.manage']
        : ['files.all.manage'];
    const canView = useAccess(viewPermissions);
    const canCreate = useAccess(createPermissions);

    const canEdit =
        useAccess(editPermissions) &&
        (documentAccess === 'manager' || documentAccess === 'editor' || documentAccess === null);
    const canManage = useAccess(managePermissions) && (documentAccess === 'manager' || documentAccess === null);

    const historyBtn = History(row);
    const copyBtn = Copy(row, fetchData);
    const moveBtn = Move(row, fetchData);
    const renameBtn = Rename(row, fetchData);
    const deleteBtn = Delete(row, fetchData);
    const sharingBtn = Sharing(row, fetchData);
    const publicLinkBtn = PublicLink(row as Document, fetchData);
    const versionBtn = Versions(row as Document);
    const downloadBtn = Download(row as Document);

    if ((row as Category).is_locked) return <></>;

    let items: MenuItemType[] = [];

    if (isDocument(row)) {
        items.push(versionBtn);

        if (canCreate) {
            items.push(copyBtn);
        }
    }
    if (canView) {
        items.push(downloadBtn);
    }
    if (canEdit) {
        items.push(renameBtn);
    }
    if (canManage) {
        items.push(sharingBtn);
        if (isDocument(row)) {
            items.push(publicLinkBtn);
        }
        items.push(deleteBtn, historyBtn, moveBtn);
    }
    if (filterActions) {
        items = filterActions(items);
    }

    return (
        <>
            {loading ? (
                <Spin spinning={loading} />
            ) : (
                items.length > 0 && (
                    <Dropdown menu={{ items }}>
                        <a onClick={(e) => e.preventDefault()}>
                            <MoreOutlined />
                        </a>
                    </Dropdown>
                )
            )}
        </>
    );
};

export default ItemActionList;
